// Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
// See LICENSE in the project root for license information.
import tableStyles from '../styles/tableView.module.css';
import globalStyles from '../styles/global.module.css';
import { getStatusClassName } from './statusHelpers';
export function createTableViewController(options) {
    let tableOpOrder = [];
    let lastTableAnchorName;
    let lastTableAnchorCoord;
    let lastTablePhases = [];
    let lastTablePackages = [];
    const buildPivotData = () => {
        const allPhases = new Set();
        for (const op of options.getOperations().values()) {
            const phaseName = op.phaseName || '(none)';
            allPhases.add(phaseName);
        }
        const phases = Array.from(allPhases).sort();
        const filteredOps = options.getFilteredOperations();
        const packageMap = new Map();
        for (const op of filteredOps) {
            const packageName = op.packageName || '(unknown package)';
            const phaseName = op.phaseName || '(none)';
            let rec = packageMap.get(packageName);
            if (!rec) {
                rec = { packageName, byPhase: new Map() };
                packageMap.set(packageName, rec);
            }
            rec.byPhase.set(phaseName, op);
        }
        const packages = Array.from(packageMap.values()).sort((a, b) => a.packageName.localeCompare(b.packageName));
        return { phases, packages };
    };
    const commitSelection = (nextSelection) => {
        options.setSelection(nextSelection);
        options.onSelectionMutated();
    };
    const handleMultiSelectGroup = (e, names) => {
        const isMeta = e.metaKey || e.ctrlKey;
        const isShift = e.shiftKey;
        let nextSelection = new Set(options.getSelection());
        if (isShift && lastTableAnchorName && tableOpOrder.includes(lastTableAnchorName)) {
            if (!isMeta)
                nextSelection = new Set(nextSelection);
            names.forEach((name) => nextSelection.add(name));
        }
        else if (isMeta) {
            let anyNew = false;
            names.forEach((name) => {
                if (!nextSelection.delete(name)) {
                    nextSelection.add(name);
                    anyNew = true;
                }
            });
            if (anyNew && names.length)
                lastTableAnchorName = names[0];
        }
        else {
            nextSelection = new Set(names);
            if (names.length)
                lastTableAnchorName = names[0];
        }
        commitSelection(nextSelection);
    };
    const handlePivotCellClick = (e, opName, rowIndex, phaseIndex) => {
        if (!opName)
            return;
        const isMeta = e.metaKey || e.ctrlKey;
        const isShift = e.shiftKey;
        if (isShift && lastTableAnchorCoord) {
            const { row: anchorRow, phase: anchorPhase } = lastTableAnchorCoord;
            const rowStart = Math.min(anchorRow, rowIndex);
            const rowEnd = Math.max(anchorRow, rowIndex);
            const phaseStart = Math.min(anchorPhase, phaseIndex);
            const phaseEnd = Math.max(anchorPhase, phaseIndex);
            const rectNames = new Set();
            for (let row = rowStart; row <= rowEnd; row++) {
                const packageRecord = lastTablePackages[row];
                if (!packageRecord)
                    continue;
                for (let phase = phaseStart; phase <= phaseEnd; phase++) {
                    const phaseName = lastTablePhases[phase];
                    if (!phaseName)
                        continue;
                    const cellOp = packageRecord.byPhase.get(phaseName);
                    if (cellOp)
                        rectNames.add(cellOp.name);
                }
            }
            if (isMeta) {
                const nextSelection = new Set(options.getSelection());
                rectNames.forEach((name) => nextSelection.add(name));
                commitSelection(nextSelection);
            }
            else {
                commitSelection(rectNames);
            }
            return;
        }
        const nextSelection = new Set(options.getSelection());
        if (isMeta) {
            if (nextSelection.has(opName))
                nextSelection.delete(opName);
            else
                nextSelection.add(opName);
        }
        else {
            nextSelection.clear();
            nextSelection.add(opName);
        }
        lastTableAnchorName = opName;
        lastTableAnchorCoord = { row: rowIndex, phase: phaseIndex };
        commitSelection(nextSelection);
    };
    const renderTable = () => {
        const tableHead = options.tableHead;
        const tableBody = options.tableBody;
        const tableStats = options.tableStats;
        if (!tableHead || !tableBody || !tableStats)
            return;
        const { phases, packages } = buildPivotData();
        tableOpOrder = [];
        lastTablePhases = phases;
        lastTablePackages = packages;
        tableHead.innerHTML = '';
        const headerRow = document.createElement('tr');
        const packageHeader = document.createElement('th');
        packageHeader.textContent = 'Package';
        headerRow.appendChild(packageHeader);
        for (const phase of phases) {
            const th = document.createElement('th');
            const displayPhase = phase.replace(/^_phase:/, '');
            th.textContent = displayPhase;
            if (displayPhase !== phase)
                th.title = phase;
            th.style.cursor = 'pointer';
            th.addEventListener('click', (e) => {
                const phaseNames = [];
                for (const op of options.getOperations().values()) {
                    if ((op.phaseName || '(none)') === phase)
                        phaseNames.push(op.name);
                }
                handleMultiSelectGroup(e, phaseNames);
            });
            headerRow.appendChild(th);
        }
        tableHead.appendChild(headerRow);
        tableBody.innerHTML = '';
        let opCount = 0;
        const selection = options.getSelection();
        packages.forEach((pkg, rowIndex) => {
            const tr = document.createElement('tr');
            const namesInRow = Array.from(pkg.byPhase.values()).map((op) => op.name);
            const allSelected = !!namesInRow.length && namesInRow.every((name) => selection.has(name));
            if (allSelected)
                tr.classList.add(tableStyles.selected);
            const pkgTd = document.createElement('td');
            pkgTd.className = tableStyles.pkgCell;
            pkgTd.textContent = pkg.packageName;
            pkgTd.style.fontWeight = '600';
            pkgTd.style.cursor = 'pointer';
            pkgTd.addEventListener('click', (e) => {
                handleMultiSelectGroup(e, namesInRow);
                e.stopPropagation();
            });
            tr.appendChild(pkgTd);
            phases.forEach((phase, phaseIndex) => {
                const td = document.createElement('td');
                td.className = tableStyles.pivotCell;
                td.style.whiteSpace = 'nowrap';
                const op = pkg.byPhase.get(phase);
                if (op) {
                    opCount++;
                    const displayStatus = options.computeDisplayStatus(op);
                    const glyph = options.enabledGlyph(op);
                    const active = op.isActive
                        ? `<span class="${tableStyles.pivotActive}" title="Active">⚡</span>`
                        : '';
                    td.innerHTML = `
                <span>${options.statusEmoji(displayStatus)}</span>
                <span title="${escapeHtml(op.name)}" class="${globalStyles.statusPill} ${getStatusClassName(displayStatus)}">${escapeHtml(options.overallStatusText(displayStatus))}</span>
                <span class="${tableStyles.pivotEnabled}" title="${escapeHtml(options.buildRunPolicyText(op))}">${glyph}</span>
                ${active}
              `;
                    td.title = options.buildTooltip(op, displayStatus);
                    if (selection.has(op.name))
                        td.classList.add(tableStyles.selected);
                    td.style.cursor = 'pointer';
                    tableOpOrder.push(op.name);
                    td.addEventListener('click', (e) => {
                        handlePivotCellClick(e, op.name, rowIndex, phaseIndex);
                        e.stopPropagation();
                    });
                }
                else {
                    td.innerHTML = '<span style="opacity:.25">—</span>';
                }
                tr.appendChild(td);
            });
            tableBody.appendChild(tr);
        });
        tableStats.textContent = opCount + ' operations';
    };
    return {
        renderTable
    };
}
function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}
//# sourceMappingURL=tableView.js.map